import React, { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { PersonModelInterface } from "@/models/personModel";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import {
    User,
    Phone,
    Hash,
    IdCard,
    MapPin,
    Building2,
    Landmark,
    Users,
    Plus,
    X,
} from "lucide-react";

import PhoneNumberInput from "@/components/PhoneNumberInput";
import GuarantorPopup from "@/components/Guarantor/GuarantorPopup";

function FieldShell({
                        icon: Icon,
                        label,
                        children,
                        error,
                    }: {
    icon: any;
    label: string;
    children: React.ReactNode;
    error?: string;
}) {
    return (
        <div className="flex items-start gap-3 py-2">
            <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                <Icon className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="min-w-0 flex-1 space-y-2">
                <Label className="text-xs text-muted-foreground">{label}</Label>
                {children}
                {error ? <p className="text-xs text-destructive">{error}</p> : null}
            </div>
        </div>
    );
}

const PersonInfoForm: React.FC = () => {
    const {
        register,
        formState: { errors },
        watch,
        setValue,
    } = useFormContext<PersonModelInterface>();

    const guarantees = (watch("guarantees") as any[]) || [];

    const [openPopup, setOpenPopup] = useState(false);
    const [editGuarantorId, setEditGuarantorId] = useState<string | null>(null);

    const guarantorToEdit = useMemo(() => {
        if (!editGuarantorId) return undefined;
        return guarantees.find((g: any) => g?.id === editGuarantorId);
    }, [editGuarantorId, guarantees]);

    const deleteGuarantor = (id: string) => {
        const updated = guarantees.filter((g: any) => g?.id !== id);
        setValue("guarantees", updated as any);
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Личная информация
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-3">
                {/* ФИО (как в карточке просмотра) */}
                <div className="rounded-lg border bg-muted/20 p-3">
                    <div className="text-xs text-muted-foreground mb-2">ФИО</div>

                    <div className="grid gap-3 md:grid-cols-3">
                        <div className="space-y-2">
                            <Label>Фамилия</Label>
                            <Input
                                {...register("detailInfo.firstName", {
                                    required: "Обязательное поле",
                                    minLength: { value: 2, message: "Минимум 2 буквы" },
                                })}
                            />
                            {errors.detailInfo?.firstName?.message ? (
                                <p className="text-xs text-destructive">
                                    {errors.detailInfo.firstName.message}
                                </p>
                            ) : null}
                        </div>

                        <div className="space-y-2">
                            <Label>Имя</Label>
                            <Input
                                {...register("detailInfo.secondName", {
                                    required: "Обязательное поле",
                                    minLength: { value: 2, message: "Минимум 2 буквы" },
                                })}
                            />
                            {errors.detailInfo?.secondName?.message ? (
                                <p className="text-xs text-destructive">
                                    {errors.detailInfo.secondName.message}
                                </p>
                            ) : null}
                        </div>

                        <div className="space-y-2">
                            <Label>Отчество</Label>
                            <Input {...register("detailInfo.lastName")} />
                        </div>
                    </div>
                </div>

                {/* Поля (как Row-строки, но с инпутами) */}
                <div className="grid gap-1 md:grid-cols-2">
                    <FieldShell
                        icon={Phone}
                        label="Телефон"
                        error={errors.detailInfo?.phoneNumber?.message as any}
                    >
                        <PhoneNumberInput />
                    </FieldShell>

                    <FieldShell
                        icon={Hash}
                        label="ИНН"
                        error={errors.detailInfo?.individualNumber?.message}
                    >
                        <Input
                            {...register("detailInfo.individualNumber", {
                                required: "Обязательное поле",
                                pattern: {
                                    value: /^\d{14}$/,
                                    message: "ИНН должен состоять из 14 цифр",
                                },
                            })}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={IdCard}
                        label="ID паспорта"
                        error={errors.detailInfo?.idPassport?.message}
                    >
                        <Input
                            {...register("detailInfo.idPassport", {
                                required: "Обязательное поле",
                            })}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={Landmark}
                        label="Город"
                        error={errors.detailInfo?.city?.message}
                    >
                        <Input
                            {...register("detailInfo.city", {
                                required: "Обязательное поле",
                                minLength: { value: 2, message: "Минимум 2 символа" },
                            })}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={MapPin}
                        label="Адрес"
                        error={errors.detailInfo?.address?.message}
                    >
                        <Input
                            {...register("detailInfo.address", {
                                required: "Обязательное поле",
                                minLength: { value: 2, message: "Минимум 2 символа" },
                            })}
                        />
                    </FieldShell>

                    <FieldShell
                        icon={Building2}
                        label="Место работы"
                        error={errors.detailInfo?.placeOfWork?.message}
                    >
                        <Input
                            {...register("detailInfo.placeOfWork", {
                                required: "Обязательное поле",
                                minLength: { value: 2, message: "Минимум 2 символа" },
                            })}
                        />
                    </FieldShell>
                </div>

                <Separator />

                {/* Поручители (как в карточке просмотра) */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        Поручители
                    </div>

                    {guarantees.length === 0 ? (
                        <div className="text-sm text-muted-foreground">—</div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {guarantees.map((g: any) => (
                                <Badge
                                    key={g?.id}
                                    variant="secondary"
                                    className="py-1 flex items-center gap-2"
                                >
                                    <button
                                        type="button"
                                        className="text-left"
                                        onClick={() => {
                                            setEditGuarantorId(g?.id ?? null);
                                            setOpenPopup(true);
                                        }}
                                    >
                                        {g?.role || "Поручитель"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => deleteGuarantor(g?.id)}
                                        className="opacity-70 hover:opacity-100"
                                        aria-label="Удалить поручителя"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}

                    <Button
                        type="button"
                        variant="outline"
                        className="mt-2"
                        onClick={() => {
                            setEditGuarantorId(null);
                            setOpenPopup(true);
                        }}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Добавить поручителя
                    </Button>

                    <GuarantorPopup
                        open={openPopup}
                        onClose={() => setOpenPopup(false)}
                        onSave={(guarantor: any) => {
                            if (editGuarantorId) {
                                const updated = guarantees.map((g: any) =>
                                    g?.id === editGuarantorId ? guarantor : g
                                );
                                setValue("guarantees", updated as any);
                                setEditGuarantorId(null);
                            } else {
                                setValue("guarantees", [...guarantees, guarantor] as any);
                            }
                        }}
                        initialData={guarantorToEdit}
                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default PersonInfoForm;
